/**
 * 底部导航（共用）
 * ============================================================
 * 抽成组件的原因：原先导航写死在 CoursePage 里，
 * 结果每个页面各写一套，切页时高亮状态还不一致。
 *
 * ── 一级导航的构成 ──────────────────────────────────────────
 * 需求文档 V1.0 列的是「首页 / 学习 / 翻译 / 发现 / 我的」共 5 项。
 * 这里多加一项「对话」，共 6 项 —— 这是有意的偏离：
 *
 *   「场景对话」是**独立的学习形态**（用真实交流练，不是按课走），
 *   和「学习」（按课程地图走）并列，塞进任何一项底下都不合适。
 *   放进「翻译」更不对 —— 翻译是单向查说法，对话是双向交流。
 *
 * 6 项在窄屏上每个约 62px，图标缩到 24px、字号 10px 仍可点。
 * 如果以后再加第 7 项，就应该改成「更多」二级菜单，不要再挤。
 */

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getUpdateState, subscribeUpdate } from '../lib/update';

const TABS = [
  { to: '/', icon: 'home', label: '首页', match: (p) => p === '/' },
  {
    to: '/courses',
    icon: 'school',
    label: '学习',
    match: (p) =>
      p.startsWith('/courses') || p.startsWith('/lesson') || p.startsWith('/session'),
  },
  {
    // 场景对话：兴趣采集 → 场景 → 词汇 → 方言对话（含水平自适应）
    to: '/conversation',
    icon: 'forum',
    label: '对话',
    match: (p) => p.startsWith('/conversation'),
  },
  {
    to: '/translate',
    icon: 'translate',
    label: '翻译',
    // 收藏夹是从翻译页进去的，高亮时一并算作翻译，避免点进去导航失去高亮
    match: (p) => p.startsWith('/translate') || p.startsWith('/saved'),
  },
  {
    to: '/radio',
    icon: 'radio',
    label: '电台',
    match: (p) => p.startsWith('/radio'),
  },
  { to: '/profile', icon: 'person', label: '我的', match: (p) => p.startsWith('/profile') },
];

export default function BottomNav() {
  const { pathname } = useLocation();

  /*
   * 更新红点：只由「壳版本落后」触发（网页层自动更新，不提示）。
   * 状态来自 lib/update.js 的单例，这里订阅它，任一页刷新后都能反映到导航栏。
   */
  const [hasUpdate, setHasUpdate] = useState(() => getUpdateState().hasUpdate);
  useEffect(() => subscribeUpdate((s) => setHasUpdate(s.hasUpdate)), []);

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-30 mx-auto flex max-w-md items-center border-t border-zinc-200 bg-white pt-2 dark:border-zinc-700 dark:bg-zinc-900"
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      {TABS.map((t) => {
        const active = t.match(pathname);
        // 「我的」标签在有新壳可装时加小红点
        const dot = t.to === '/profile' && hasUpdate;
        return (
          <Link
            key={t.to}
            to={t.to}
            className="relative flex min-w-0 flex-1 flex-col items-center gap-0.5 px-0.5"
            aria-current={active ? 'page' : undefined}
          >
            {/* 6 项比 5 项挤，图标从 26 缩到 23、字号从 11 缩到 10，仍保证可点 */}
            <span
              className={`material-icons text-[23px] ${active ? 'text-teal-500' : 'text-[#9e9e9e]'}`}
            >
              {t.icon}
            </span>
            {dot && (
              <span className="pointer-events-none absolute right-1/2 top-0 h-2 w-2 translate-x-3 rounded-full bg-rose-500 ring-2 ring-white dark:ring-zinc-900" />
            )}
            <span
              className={`truncate text-[10px] ${active ? 'text-teal-500' : 'text-[#9e9e9e]'}`}
            >
              {t.label}
            </span>
          </Link>
        );
      })}
    </nav>
  );
}
